/**
 * User Management Page - AWLP Design System
 * List users with accordion cards, search, and CRUD operations
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Plus, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Avatar } from '../../components/ui/Avatar';
import { Card } from '../../components/ui/Card';
import { WarningToast, useAutoDismissWarning } from '../../components/ui/WarningToast';
import userService, { Member } from '../../services/api/userService';
import { useAuthStore } from '../../stores/authStore';
import { CreateUserModal } from './CreateUserModal';
import { EditUserModal } from './EditUserModal';
import { ConfirmationDialog } from './ConfirmationDialog';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 400px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const UserCard = styled(Card)`
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.base};
`;

const UserCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const UserAvatar = styled(Avatar)``;

const UserInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const UserName = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const UserEmail = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const UserMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
`;

const ExpandIcon = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const UserCardBody = styled.div<{ $isExpanded: boolean }>`
  display: ${({ $isExpanded }) => ($isExpanded ? 'block' : 'none')};
  padding: ${({ theme }) => theme.spacing[4]};
  padding-top: 0;
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const DetailLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DetailValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DetailBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PaginationInfo = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

export const UserManagementPage = () => {
  const { hasPermission } = useAuthStore();
  // Deactivate/Reactivate disable the member's Entra sign-in, so they require the
  // org-admin-only 'team:manage:actions' permission. This route itself only requires
  // 'team:manage', which Clinical Directors and BCBAs also hold.
  const canDeactivate = hasPermission('team:manage:actions');

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<Member | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    user: Member | null;
    action: 'deactivate' | 'reactivate';
  }>({ isOpen: false, user: null, action: 'deactivate' });

  const queryClient = useQueryClient();
  // Surface lifecycle failures (e.g. sign-in could not be re-enabled at the identity
  // provider, so the member was NOT reactivated) instead of failing silently.
  const { warning: errorMsg, showWarning: showError, clear: clearError } =
    useAutoDismissWarning('Action failed. Please try again.', 10000);

  const { data: membersData, isLoading } = useQuery({
    queryKey: ['members', searchQuery],
    queryFn: () => userService.getMembers({ search: searchQuery }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => userService.deactivateMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setConfirmationDialog({ isOpen: false, user: null, action: 'deactivate' });
    },
    onError: (err) => showError(err),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => userService.reactivateMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setConfirmationDialog({ isOpen: false, user: null, action: 'reactivate' });
    },
    onError: (err) => showError(err),
  });

  const handleToggleExpand = (userId: string) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  const handleEdit = (user: Member) => {
    setEditingUser(user);
  };

  const handleDeactivate = (user: Member) => {
    setConfirmationDialog({ isOpen: true, user, action: 'deactivate' });
  };

  const handleReactivate = (user: Member) => {
    setConfirmationDialog({ isOpen: true, user, action: 'reactivate' });
  };

  const handleConfirmAction = () => {
    if (!confirmationDialog.user) return;

    if (confirmationDialog.action === 'deactivate') {
      deactivateMutation.mutate(confirmationDialog.user.id);
    } else {
      reactivateMutation.mutate(confirmationDialog.user.id);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Container>
      <WarningToast message={errorMsg} onDismiss={clearError} variant="error" />
      <Header>
        <Title>User Management</Title>
        <SearchContainer>
          <Input
            type="search"
            placeholder="Search users..."
            leftIcon={<Search />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
          />
        </SearchContainer>
        <Button
          variant="primary-blue"
          leftIcon={<Plus />}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Add User
        </Button>
      </Header>

      {isLoading ? (
        <EmptyState>Loading users...</EmptyState>
      ) : !membersData?.data?.length ? (
        <EmptyState>No users found</EmptyState>
      ) : (
        <>
          <UserList>
            {membersData.data.map((user) => {
              const isExpanded = expandedUserId === user.id;

              return (
                <UserCard key={user.id} padding="none">
                  <UserCardHeader onClick={() => handleToggleExpand(user.id)}>
                    <UserAvatar firstName={user.firstName} lastName={user.lastName} size="lg" />
                    <UserInfo>
                      <UserName>
                        {user.firstName} {user.lastName}
                      </UserName>
                      <UserEmail>{user.email}</UserEmail>
                    </UserInfo>
                    <UserMeta>
                      {user.roles?.map((role) => (
                        <Badge key={role.id} roleName={role.name}>
                          {role.name}
                        </Badge>
                      ))}
                      <Badge variant={user.status === 'active' ? 'success' : 'neutral'}>
                        {user.status}
                      </Badge>
                    </UserMeta>
                    <ExpandIcon>{isExpanded ? <ChevronUp /> : <ChevronDown />}</ExpandIcon>
                  </UserCardHeader>

                  <UserCardBody $isExpanded={isExpanded}>
                    <DetailsGrid>
                      <DetailSection>
                        <DetailLabel>Contact Information</DetailLabel>
                        <DetailValue>Email: {user.email}</DetailValue>
                        <DetailValue>
                          Full Name: {user.firstName} {user.lastName}
                        </DetailValue>
                        <DetailValue>Created: {formatDate(user.createdAt)}</DetailValue>
                      </DetailSection>

                      <DetailSection>
                        <DetailLabel>Roles & Permissions</DetailLabel>
                        <DetailBadges>
                          {user.roles?.map((role) => (
                            <Badge key={role.id} roleName={role.name}>
                              {role.name}
                            </Badge>
                          ))}
                        </DetailBadges>
                        <DetailValue>Status: {user.status}</DetailValue>
                      </DetailSection>

                      <DetailSection>
                        <DetailLabel>Groups</DetailLabel>
                        <DetailBadges>
                          {user.groups && user.groups.length > 0 ? (
                            user.groups.map((group) => (
                              <Badge key={group.id} variant="neutral">
                                {group.name}
                              </Badge>
                            ))
                          ) : (
                            <DetailValue>No groups assigned</DetailValue>
                          )}
                        </DetailBadges>
                      </DetailSection>
                    </DetailsGrid>

                    <ActionButtons>
                      <Button variant="secondary" size="sm" onClick={() => handleEdit(user)}>
                        Edit
                      </Button>
                      {canDeactivate &&
                        (user.status === 'active' ? (
                          <Button variant="danger" size="sm" onClick={() => handleDeactivate(user)}>
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleReactivate(user)}
                          >
                            Reactivate
                          </Button>
                        ))}
                    </ActionButtons>
                  </UserCardBody>
                </UserCard>
              );
            })}
          </UserList>

          {membersData.meta && (
            <PaginationInfo>
              Showing {membersData.data.length} of {membersData.meta.total} users
            </PaginationInfo>
          )}
        </>
      )}

      <CreateUserModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />

      {editingUser && (
        <EditUserModal
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          user={editingUser}
        />
      )}

      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onClose={() => setConfirmationDialog({ isOpen: false, user: null, action: 'deactivate' })}
        onConfirm={handleConfirmAction}
        user={confirmationDialog.user}
        action={confirmationDialog.action}
        isLoading={deactivateMutation.isPending || reactivateMutation.isPending}
      />
    </Container>
  );
};

export default UserManagementPage;
